import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { CASE_STUDY_IMAGE_BY_SLUG } from "../data/content";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  "All",
  "Major Events",
  "University Operations",
  "Student Experience",
  "Systems",
  "Community Programs",
  "Accessibility",
];

export default function CaseStudies() {
  useDocumentMeta({
    title: "Case Studies · Programs that keep running — JJ Argent",
    description:
      "Senior case studies: scaling a university food-relief program to 8,000+ weekly registrations, Calgary Pride at 145,000+ attendees, and rebuilding U of T ancillary services.",
    path: "/case-studies",
  });
  const [filter, setFilter] = useState("All");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/case-studies`).then((r) => {
      setItems(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const shown = useMemo(
    () => (filter === "All" ? items : items.filter((c) => (c.tags || []).includes(filter))),
    [filter, items]
  );

  return (
    <main id="main" className="bg-[#F3EFE6] text-[#0B0B0C]" data-testid="case-studies-page">
      {/* hero */}
      <section className="pt-40 pb-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="eyebrow !text-[#B98B24]">Case studies</p>
          <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.04em]">
            Impact that lasts.{" "}<br />
            <span className="text-[#343437]">Programs that keep running.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#343437] leading-relaxed">
            Every case study below is grounded in real work — universities, festivals and community programs
            across Australia and Canada.
          </p>
        </div>
      </section>

      {/* filters */}
      <section className="border-t border-[#0B0B0C]/15 bg-[#FAF9F6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 flex flex-wrap gap-2 items-center">
          <span className="eyebrow !text-[#B98B24] mr-3">Filter</span>
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`cs-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-3.5 py-2 font-display text-[11px] tracking-[0.16em] uppercase border transition-colors ${
                filter === f
                  ? "border-[#0B0B0C] text-[#0B0B0C] bg-[#0B0B0C]/5"
                  : "border-[#0B0B0C]/25 text-[#343437] hover:border-[#0B0B0C] hover:text-[#0B0B0C]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </section>

      {/* grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {loading && <div className="text-[#343437] text-sm py-8" data-testid="cs-loading">Loading case studies…</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#0B0B0C]/15 border border-[#0B0B0C]/15">
            {shown.map((c, i) => (
              <motion.article
                key={c.id}
                initial={{ y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.06, ease: [0.16, 0.84, 0.24, 1] }}
                className="relative bg-[#F3EFE6] hover:bg-[#FAF9F6] hover:z-10 hover:-translate-y-1 hover:shadow-[0_20px_40px_-24px_rgba(11,11,12,0.35)] transition-all duration-500 flex flex-col group"
                data-testid={`case-study-${c.id}`}
              >
                {CASE_STUDY_IMAGE_BY_SLUG[c.slug] && (
                  <div className="relative aspect-[16/9] overflow-hidden border-b border-[#0B0B0C]/15 bg-[#151517]">
                    <img
                      src={CASE_STUDY_IMAGE_BY_SLUG[c.slug]}
                      alt={c.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                    />
                  </div>
                )}
                <div className="p-8 lg:p-10 flex flex-col flex-1">
                <div className="flex items-center justify-between">
                  <span className="eyebrow !text-[#B98B24]">{c.status}</span>
                  <span className="text-xs tracking-widest uppercase text-[#343437]">{c.org}</span>
                </div>
                <h3 className="mt-6 font-display text-2xl md:text-3xl tracking-tight leading-tight text-[#0B0B0C]">
                  {c.title}
                </h3>
                <p className="mt-4 text-[#343437] leading-relaxed flex-1">{c.summary}</p>
                <div className="mt-6 border-t border-[#0B0B0C]/15 pt-4">
                  <p className="text-xs tracking-widest uppercase text-[#B98B24] mb-2">Scale</p>
                  <p className="text-sm text-[#0B0B0C]">{c.scale}</p>
                </div>
                <div className="mt-6 flex flex-wrap gap-2">
                  {c.tags.map((t) => (
                    <span key={t} className="text-[10px] tracking-[0.16em] uppercase text-[#343437] border border-[#0B0B0C]/25 px-2 py-1 transition-colors duration-300 group-hover:border-[#B98B24] group-hover:text-[#B98B24]">
                      {t}
                    </span>
                  ))}
                </div>
                <Link
                  to={`/case-studies/${c.slug}`}
                  data-testid={`cs-open-${c.slug}`}
                  className="mt-8 inline-flex items-center gap-2 font-display text-[12px] tracking-[0.18em] uppercase text-[#0B0B0C] group-hover:text-[#B98B24] transition-colors"
                >
                  Read the story <ArrowUpRight size={14} />
                </Link>
                </div>
              </motion.article>
            ))}
          </div>
          {shown.length === 0 && (
            <p className="text-center py-16 text-[#343437]">No case studies match this filter yet.</p>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0B0B0C] text-[#FAF9F6] py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight max-w-2xl">
            Have a program that shouldn't rely on one person?
          </h2>
          <Link to="/contact" data-testid="cs-cta-contact" className="btn-primary">
            Let's talk <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
